// Phase C1A — per-route SEO metadata (FR-static, single source of truth).
// Rendered client-side by <Seo> via React 19 native metadata hoisting.
//
// C1A emits ONLY title / description / canonical / robots. OG/Twitter are NOT
// rendered here — they remain the static C0 fallback in index.html until Phase B.
// `ogImage` is PREPARED for Phase B but intentionally NOT emitted in C1A.

import { SERVICE_CATALOG, servicePath, serviceSeoKey } from '../navigation/serviceCatalog.js';

export const SEO_SITE_URL = 'https://blackmatte.fr';
const abs = (path) => `${SEO_SITE_URL}${path}`;

export const seo = {
  // ---- INDEXABLE (title + description + self canonical) ----
  home: {
    title: 'Black Matte — VTC Premium à Nantes | Chauffeur privé',
    description:
      'Chauffeur privé premium à Nantes : transferts aéroport, trajets pro et perso. Véhicules Mercedes, tarifs fixes dès 35 €.',
    canonical: abs('/'),
    ogImage: '/og-home.png', // PREPARED for Phase B — not emitted in C1A
  },
  services: {
    title: 'Services VTC à Nantes — aéroport, gare, pro | Black Matte',
    description:
      'Transferts aéroport Nantes Atlantique, gare SNCF, déplacements professionnels et événements. VTC premium en Loire-Atlantique.',
    canonical: abs('/services'),
    ogImage: '/og-services.png',
  },
  entreprises: {
    title: 'VTC Entreprise à Nantes — compte pro, facturation | Black Matte',
    description:
      'Solution VTC pour entreprises : tarifs négociés, facturation mensuelle, compte dédié et reporting. Simplifiez les trajets de vos collaborateurs.',
    canonical: abs('/entreprises'),
    ogImage: '/og-business.png',
  },
  contact: {
    title: 'Contact — Black Matte VTC Nantes',
    description:
      'Contactez Black Matte VTC à Nantes : devis, réservation, questions. Disponible 24h/24, 7j/7 — Nantes et Loire-Atlantique.',
    canonical: abs('/contact'),
    ogImage: '/og-contact.png',
  },
  about: {
    title: 'À propos — Black Matte VTC Nantes',
    description:
      'Black Matte, service de chauffeur privé premium à Nantes et en Loire-Atlantique : transport fiable, ponctuel et professionnel.',
    canonical: abs('/about'),
    // ogImage omitted → og-default fallback until a dedicated About OG asset exists (OG Phase 2).
  },
  confidentialite: {
    title: 'Politique de confidentialité — Black Matte',
    description:
      'Politique de confidentialité et protection des données (RGPD) de Black Matte VTC, Nantes.',
    canonical: abs('/confidentialite'),
    ogImage: '/og-privacy.png',
  },
  cgu: {
    title: "Conditions Générales d'Utilisation — Black Matte",
    description:
      'CGU de Black Matte, service VTC à Nantes : accès, réservation, paiement, responsabilités.',
    canonical: abs('/legal/cgu'),
    ogImage: '/og-cgu.png',
  },
  mentionsLegales: {
    title: 'Mentions Légales & RGPD — Black Matte',
    description:
      'Mentions légales et politique RGPD de Black Matte VTC, Nantes (44), France.',
    canonical: abs('/legal/mentions-legales'),
    ogImage: '/og-legal.png',
  },
  refundPolicy: {
    title: "Politique d'Annulation et Remboursement — Black Matte",
    description:
      'Conditions d’annulation et de remboursement Black Matte : gratuit +2 h avant, frais tardifs, no-show.',
    canonical: abs('/legal/refund-policy'),
    ogImage: '/og-refund.png',
  },
  // Legacy /mentions-legales → cross-canonical to the preferred legal route (NOT self).
  mentionsLegalesLegacy: {
    title: 'Mentions Légales & RGPD — Black Matte',
    description:
      'Mentions légales et politique RGPD de Black Matte VTC, Nantes (44), France.',
    canonical: abs('/legal/mentions-legales'),
    ogImage: '/og-legal.png',
  },

  // ---- NOINDEX (title + robots noindex; no canonical / no OG) ----
  reserver: { title: 'Réservation — Black Matte', noindex: true },
  paymentSuccess: { title: 'Paiement — Black Matte', noindex: true },
  devisValidate: { title: 'Validation du devis — Black Matte', noindex: true },
  tracking: { title: 'Suivi de course — Black Matte', noindex: true },
  driver: { title: 'Espace chauffeur — Black Matte', noindex: true },
  company: { title: 'Espace entreprise — Black Matte', noindex: true },
  corporateInvite: { title: 'Invitation entreprise — Black Matte', noindex: true },
  partner: { title: 'Espace partenaire — Black Matte', noindex: true },
  admin: { title: 'Administration — Black Matte', noindex: true },
  chauffeurReglement: { title: 'Règlement chauffeurs — Black Matte', noindex: true },
  chauffeurContract: { title: 'Contrat partenaire chauffeur — Black Matte', noindex: true },
};

// WP-3.3 — per-service metadata derived from the ratified SVC-2 catalogue. Title/description assemble
// ratified facts (service name + SAB service area + brand); no invented marketing copy. The Owner may
// later localize/refine these strings (content production).
for (const s of SERVICE_CATALOG) {
  seo[serviceSeoKey(s.slug)] = {
    title: `${s.name} — Black Matte VTC Nantes`,
    description: `${s.name} with a private chauffeur by Black Matte — serving Nantes, Angers, Rennes and La Roche-sur-Yon.`,
    canonical: abs(servicePath(s.slug)),
    ogImage: '/og-services.png',
  };
}

// Service Content Phase 1 — Airport Transfer (flagship). The generic English template above is too
// weak for the canonical-French site and the highest-intent airport queries (transfert aéroport
// Nantes / VTC aéroport Nantes Atlantique). Override with French metadata built from facts already
// live on the site (real-time flight tracking, fixed price, 24/7, online booking). Canonical and OG
// asset are unchanged; only the title/description are strengthened.
seo[serviceSeoKey('airport-transfer')] = {
  title: 'Transfert aéroport Nantes Atlantique — VTC | Black Matte',
  description:
    'Transfert aéroport Nantes Atlantique avec chauffeur privé : suivi des vols, tarif fixe et réservation en ligne. VTC premium disponible 24h/24, 7j/7.',
  canonical: abs(servicePath('airport-transfer')),
  ogImage: '/og-services.png',
};

// Service Content (Station Transfer). French metadata targeting the station intent (transfert gare
// Nantes / VTC gare SNCF Nantes); facts already live on the site (gare SNCF de Nantes transfers, fixed
// price, punctuality, online booking, 24/7). Canonical and OG asset unchanged.
seo[serviceSeoKey('station-transfer')] = {
  title: 'Transfert gare SNCF Nantes — VTC | Black Matte',
  description:
    'Transfert gare SNCF de Nantes avec chauffeur privé : tarif fixe, ponctualité et réservation en ligne. VTC premium disponible 24h/24, 7j/7.',
  canonical: abs(servicePath('station-transfer')),
  ogImage: '/og-services.png',
};

// Service Content (Business Chauffeur). French metadata targeting the business intent (chauffeur
// d'affaires / VTC entreprise Nantes / déplacement professionnel); facts already live on the site
// (déplacements professionnels, rendez-vous d'affaires, ponctualité, discrétion, fixed price, online
// booking, 24/7). No hard B2B promises. Canonical and OG asset unchanged.
seo[serviceSeoKey('business-chauffeur')] = {
  title: 'Chauffeur d’affaires Nantes — VTC entreprise | Black Matte',
  description:
    'Chauffeur d’affaires à Nantes pour vos déplacements professionnels : ponctualité, discrétion, tarif fixe et réservation en ligne. VTC entreprise 24h/24.',
  canonical: abs(servicePath('business-chauffeur')),
  ogImage: '/og-services.png',
};

// Service Content (Chauffeur by the Hour / mise à disposition). French metadata targeting the
// mise-à-disposition intent (chauffeur à disposition / à l'heure Nantes); facts live on the site
// (mise à disposition de véhicule avec chauffeur, événements/journées business, ponctualité,
// discrétion, online booking). No hourly rate / duration package / guaranteed-availability claim.
seo[serviceSeoKey('chauffeur-by-the-hour')] = {
  title: 'Chauffeur à disposition Nantes — à l’heure | Black Matte',
  description:
    'Chauffeur privé à disposition à Nantes, à l’heure, pour vos rendez-vous et événements : flexibilité, ponctualité, discrétion. Réservation en ligne.',
  canonical: abs(servicePath('chauffeur-by-the-hour')),
  ogImage: '/og-services.png',
};

// Service Content (Event Chauffeur). French metadata targeting the event intent (chauffeur événement /
// transport événementiel Nantes); facts live on the site (transport événementiel, événements pro et
// privés, séminaires/conférences/soirées, ponctualité, discrétion, online booking). Transport only —
// no event-planning/coordination/VIP/fleet claim. Canonical and OG asset unchanged.
seo[serviceSeoKey('event-chauffeur')] = {
  title: 'Chauffeur événement Nantes — VTC événementiel | Black Matte',
  description:
    'Chauffeur privé pour vos événements à Nantes : séminaires, conférences et soirées. Transport événementiel ponctuel et discret, réservation en ligne.',
  canonical: abs(servicePath('event-chauffeur')),
  ogImage: '/og-services.png',
};

// Service Content (Hotel Transfer). French metadata targeting the hotel-transfer intent (transfert
// hôtel / chauffeur hôtel Nantes). Transport only — pickup/drop-off at the hotel; NO partnership /
// concierge / reception / luggage-handling claim. Canonical and OG asset unchanged.
seo[serviceSeoKey('hotel-transfer')] = {
  title: 'Transfert hôtel Nantes — chauffeur privé | Black Matte',
  description:
    'Transfert hôtel à Nantes avec chauffeur privé : prise en charge et dépose à votre hôtel, ponctualité, discrétion et réservation en ligne.',
  canonical: abs(servicePath('hotel-transfer')),
  ogImage: '/og-services.png',
};

// Service Content (Long-Distance Transfer). French metadata targeting the long-distance intent
// (chauffeur / VTC longue distance Nantes). Generic intercity transport from the service area — NO
// named route / city pricing / toll / duration claim. Canonical and OG asset unchanged.
seo[serviceSeoKey('long-distance-transfer')] = {
  title: 'Chauffeur longue distance Nantes — VTC | Black Matte',
  description:
    'Chauffeur privé longue distance au départ de Nantes : trajets confortables partout en France. Ponctualité, discrétion et réservation en ligne.',
  canonical: abs(servicePath('long-distance-transfer')),
  ogImage: '/og-services.png',
};
